import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormGroup,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { FacetFilter } from '@app/new-search/models/facet-filter.model';
import { isFacetFilterSelected } from '@app/new-search/utils/new-search-filters.util';
import { ModalController } from '@ionic/angular';
import { Facet, FacetValue } from '@usf/product-types';
import { TranslateService } from '@ngx-translate/core';

@Component({
  template: '',
})
export class NewSearchFiltersBaseComponent implements OnInit {
  @Input() facets: Facet[];
  @Input() selectedFacet: Facet;
  @Input() totalProductCount: number;
  @Input() selectedFacetFilters: FacetFilter[];
  @Output() updateSelectedFacetFilters: EventEmitter<
    FacetFilter[]
  > = new EventEmitter();

  formGroup: FormGroup;
  facetFormGroup: FormGroup;
  appliedFacetValueLabelsMap = new Map<string, string>();

  constructor(
    protected modalController: ModalController,
    private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.createNewSearchForm();
    this.facetFormGroup = this.formGroup?.controls['facets'] as FormGroup;
    this.buildFacetsAppliedLabel();
  }

  setSelectedFacet(facet: Facet) {
    this.selectedFacet = facet;
  }

  async apply() {
    this.selectedFacet = null;
    let selectedFacetFilters: FacetFilter[] = [];
    Object.keys(this.facetFormGroup.controls).forEach(fieldName => {
      const abstractControl = this.facetFormGroup.controls[fieldName];
      selectedFacetFilters = [
        ...selectedFacetFilters,
        ...this.getSelectedFacetFilters(
          abstractControl as UntypedFormGroup,
          fieldName,
        ),
      ];
    });
    await this.modalController.dismiss(selectedFacetFilters);
  }

  async clearAll() {
    this.selectedFacet = null;
    this.getSelectedFacetFilters(this.facetFormGroup, null, true);
    await this.modalController.dismiss([]);
  }

  createNewSearchForm() {
    this.formGroup = this.formBuilder.group({
      facets: this.formBuilder.group({}),
    });
    const facets = this.formGroup.get('facets') as UntypedFormGroup;
    this.facets?.forEach(facet => {
      facets.addControl(`${facet.fieldName}`, new UntypedFormGroup({}));
      const formGroup = facets.get(`${facet.fieldName}`) as UntypedFormGroup;

      facet.values.forEach(facetValue => {
        this.addFormControl(facet.fieldName, facetValue, formGroup);
      });
    });
  }

  addFormControl(
    fieldName: string,
    facetValue: FacetValue,
    formGroup: UntypedFormGroup,
  ) {
    if (facetValue?.childrenFacetValues?.length > 0) {
      const newFormGroup = new UntypedFormGroup({});
      formGroup.addControl(`${facetValue.value}`, newFormGroup);
      this.addFormControl(
        fieldName,
        { ...facetValue, label: 'Select All', childrenFacetValues: null },
        newFormGroup,
      );
      facetValue.childrenFacetValues.forEach(childFacetValue => {
        this.addFormControl(fieldName, childFacetValue, newFormGroup);
      });
    } else {
      formGroup.addControl(
        `${facetValue.value}`,
        new UntypedFormControl(
          isFacetFilterSelected(this.selectedFacetFilters, {
            fieldName,
            facetValue,
          }),
        ),
      );
    }
  }

  getSelectedFacetFilters(
    formGroup: UntypedFormGroup,
    fieldName: string,
    unselectFacetValues: boolean = false,
  ) {
    let selectedFacetFilters: FacetFilter[] = [];
    Object.keys(formGroup.controls).forEach(key => {
      const abstractControl = formGroup.controls[key];

      if (abstractControl instanceof UntypedFormGroup) {
        selectedFacetFilters = [
          ...selectedFacetFilters,
          ...this.getSelectedFacetFilters(
            abstractControl as UntypedFormGroup,
            fieldName,
            unselectFacetValues,
          ),
        ];
      } else if (abstractControl instanceof UntypedFormControl) {
        if (abstractControl.value === true) {
          selectedFacetFilters = [
            ...selectedFacetFilters,
            { fieldName, facetValue: { value: key } as FacetValue },
          ];

          if (unselectFacetValues) {
            abstractControl.setValue(false);
          }
        }
      }
    });
    return selectedFacetFilters;
  }

  buildFacetsAppliedLabel() {
    this.selectedFacetFilters?.forEach(f => {
      if (f?.facetValue?.isApplied) {
        const valueMap = this.appliedFacetValueLabelsMap.get(f?.fieldName)
          ? this.appliedFacetValueLabelsMap.get(f?.fieldName) +
            ',' +
            f?.facetValue?.label
          : f?.facetValue?.label;
        this.appliedFacetValueLabelsMap.set(f?.fieldName, valueMap);
      }
    });
    this.appliedFacetValueLabelsMap.forEach((value, key) => {
      if (value.length > 52) {
        this.appliedFacetValueLabelsMap.set(
          key,
          this.appliedFacetValueLabelsMap
            ?.get(key)
            .split(',')
            .length.toString()
            .concat(
              ' ' +
                this.translateService.instant(
                  'i18n.search.searchFilters.selected',
                ),
            ),
        );
      }
    });
  }
}
